
import { computed, defineComponent, onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import { formatDate } from "@/core/helpers/DatetimeConverter";
import PaginationBar from "@/components/list/parts/PaginationBar.vue";
import Dropdown1 from "@/components/dropdown/Dropdown1.vue";
import Popper from "vue3-popper";
import { useRoute } from "vue-router";

import Swal from "sweetalert2/dist/sweetalert2.js";

export default defineComponent({
  name: "TableWidget",
  components: { PaginationBar, Dropdown1, Popper },

  props: {
    widgetClasses: String,
    routeKey: String,
  },
  setup(props) {

    const store = useStore();
    const route = useRoute();
    const page = ref(1);
    const isCallingApi = ref(false);

    const getPaginationParams = computed(() => {
      return store.getters.getPaginationParams({ key: props.routeKey });
    });
    const getSortBy = computed(() => {
      return store.getters.getSortBy({ key: props.routeKey });
    });

    const sortableClicked = (event, columnName) => {
      if (event.target.className.includes("sortable")) {
        let tempColumnType = "";
        if (event.target.className.includes("asc")) {
          tempColumnType = "desc";
        } else {
          tempColumnType = "asc";
        }
        store.dispatch(Actions.SET_SORT_BY, {
          sortBy: {
            sortColumn: columnName,
            sortType: tempColumnType,
          },
          key: props.routeKey,
        });
        store.dispatch(Actions.GET_DYNAMIC_LIST, {
          key: props.routeKey,
          page: 1,
        });
      }
    };

    const toggleModal = (id) => {
      store.dispatch(Actions.SETUP_FORM_COMPONENT_OR_MODAL, {
        key: props.routeKey,
        id: id,
      });

      //store.dispatch(Actions.TOGGLE_IS_MODAL_OPEN);
    };
    const removeFilter = (key, clear) => {
      store.dispatch(Actions.CLEAR_FILTER_TAG, {
        key: props.routeKey,
        attrKey: key,
      });
      if (clear) {
        clear();
      }
      //store.dispatch(Actions.TOGGLE_IS_MODAL_OPEN);
    };
    const runWithDeleteLoader = async (action: () => Promise<void>) => {
      Swal.fire({
        title: "Deleting...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      try {
        await action();
      } finally {
        Swal.close();
      }
    };

    const deleteItem = async (id, index) => {
      const deleted = await Swal.fire({
        text: "Are you sure you want to delete this item?",
        icon: "error",
        buttonsStyling: true,
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: true,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn",
        },
      });
      if (deleted.isConfirmed) {
        await runWithDeleteLoader(() =>
          store.dispatch(Actions.DELETE_ITEM, {
            key: props.routeKey,
            id: id,
            index: index,
          })
        );
      }
    };

    const paginateItems = () => {
      store.dispatch(Actions.GET_DYNAMIC_LIST, {
        key: props.routeKey,
        page: getPaginationParams.value.currentPage,
      });
    };
    const getMapping = computed(() => {
      return store.getters.getMapping({ key: props.routeKey });
    });
    const items = computed(() => {
      return store.getters.getDynamicList({ key: props.routeKey });
    });
    const handleMappedButton = async (handlerName: string, item: any) => {
      await store.dispatch(Actions.CALL_CUSTOM_API, {
        key: props.routeKey,
        handlerName,
        row: item,
      });
    };
    const resolveFooterLink = (button: any, item: any) => {
      return typeof button.to === "function" ? button.to(item) : button.to;
    };
    const getAppliedFilterValues = computed(() => {
      return store.getters.getAppliedFilterValues({ key: props.routeKey });
    });
    const getFiltersMapping = computed(() => {
      return store.getters.getFiltersMapping({ key: props.routeKey });
    });
    const getSearchTerm = computed(() => {
      return store.getters.getSearchTerm({ key: props.routeKey });
    });
    const getInlineSearch = computed(() => {
      return store.getters.getInlineSearch({ key: props.routeKey });
    });
    const pageName = computed(() => {
      return getMapping.value ? getMapping.value.map.Plural : "";
    });
    const multipleEditToggleModal = () => {
      store.dispatch(Actions.SETUP_FORM_COMPONENT_OR_MODAL, {
        key: props.routeKey,
        isMultipleUpdate: true,
      });

      //store.dispatch(Actions.TOGGLE_IS_MODAL_OPEN);
    };
    const getMultipleFormEdit = computed(() => {
      return store.getters.getMultipleFormEdit({ key: props.routeKey });
    });
    const expandedRowId = ref<number | null>(null);

    const toggleExpandedRow = (id: number) => {
      expandedRowId.value = expandedRowId.value === id ? null : id;
    };

    const localSearchTerm = ref(
      route.query
        ? route.query["SearchTerm"]
          ? route.query["SearchTerm"]
          : ""
        : ""
    );
    const typingTimer = ref<number | null>(null);
    const TYPING_DELAY = 300;

    const searchKeyUp = (e: KeyboardEvent) => {
      if (e && e.key === "Enter") {
        if (typingTimer.value) clearTimeout(typingTimer.value);
        runSearch();
        return;
      }
      if (typingTimer.value) clearTimeout(typingTimer.value);
      typingTimer.value = setTimeout(runSearch, TYPING_DELAY);
    };

    const runSearch = () => {
      if (localSearchTerm.value.length >= 1) {
        store.dispatch(Actions.SET_SEARCH_TERM, {
          searchTerm: localSearchTerm.value,
          key: props.routeKey,
        });
        if (!getInlineSearch.value) {
          store.dispatch(Actions.GET_DYNAMIC_LIST, {
            page: 1,
            key: props.routeKey,
          });
        }
      } else if (getSearchTerm.value !== "") {
        store.dispatch(Actions.SET_SEARCH_TERM, {
          searchTerm: "",
          key: props.routeKey,
        });
        if (!getInlineSearch.value) {
          store.dispatch(Actions.GET_DYNAMIC_LIST, {
            page: 1,
            key: props.routeKey,
          });
        }
      }
    };

    onUnmounted(() => {
      if (typingTimer.value) clearTimeout(typingTimer.value);
    });

   // BoxesWidget.vue setup
    const hasActions = computed(() => {
      const a = getMapping.value?.map?.Actions;
      return Boolean(a?.editLink || a?.pageLink || a?.deleteLink);
    });
    
    return {
      items,
      handleMappedButton,
      resolveFooterLink,
      getPaginationParams,
      paginateItems,
      getSortBy,
      pageName,
      page,
      formatDate,
      toggleModal,
      sortableClicked,
      deleteItem,
      getAppliedFilterValues,
      getFiltersMapping,
      localSearchTerm,
      searchKeyUp,
      removeFilter,
      getMapping,
      multipleEditToggleModal,
      getMultipleFormEdit,
      toggleExpandedRow,
      hasActions,
      expandedRowId
    };
  },
});
